import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from '@material-ui/core/Chip';
import moment from 'moment-timezone';
import { FilterHelper } from '@slx/slixta-common';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { BaseEntityPage } from '../../../common/components/BaseEntityPage';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { StaffHelper } from '../../../common/helpers/StaffHelper';
import { Controller } from '../../../common/Controller';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { CRMHelper } from '../../../helpers/CRMHelper';
import { CdpHelper } from '../../../helpers/CdpHelper';
import { AppHelper } from '../../../helpers/AppHelper';

const EXCLUDED_CHIP_KEYS = ['_labelForSlxAdId', '_labelForUtmId', '_sortKey', '_activeTab', '_sortOrder', '_datetimezone']

function generateNumberRangeValue({ startValue, endValue }) {
  if (startValue && endValue) {
    return `ranging from ${startValue} to ${endValue}`
  }
  if (startValue) {
    return `greater than or equal to ${startValue}`
  }
  if (endValue) {
    return `less than or equal to ${endValue}`
  }
  return ''
}

function resolveOptionLabel(filter, key, val) {
  const option = filter && Array.isArray(filter.options)
    ? filter.options.find((item) => item && item.key === val)
    : null
  if (option && (option.label || option.text)) {
    return option.label || option.text
  }
  const baseKey = FilterHelper.getFilterBaseKey(key)
  if (val && ['assignee', 'routedAssignee'].includes(baseKey)) {
    return '[Deleted User]'
  }
  return val
}

function buildChipLabel(chipLabel, key, value, searchFilters, timezone) {
  let keyPart = ''
  if (key === '_datetimezone') {
    keyPart = 'CRM Timezone is '
  } else if (key.endsWith('*')) {
    keyPart = <span>{chipLabel + ' contains '} </span>
  } else if (key.endsWith('!')) {
    keyPart = <span>{chipLabel + ' is not '} </span>
  } else {
    keyPart = <span>{chipLabel + ' is '}</span>
  }

  let filterValue = value
  if (!value || (Array.isArray(value) && value.length === 0)) {
    if (key && searchFilters && Object.keys(searchFilters).includes(key)) {
      filterValue = searchFilters[key]
    }
  }

  let valuePart
  const tz = timezone || Helper.getLocalTimezone()
  if (value && typeof value === 'object' && value.type === 'dateRange') {
    const dateTimezone = ['date_yyyymmdd', 'dateTime'].includes(value.subType) ? Helper.getLocalTimezone() : tz
    let dateValue = filterValue.id
    if (dateValue === 'custom') {
      if (filterValue.startValue && filterValue.endValue) {
        dateValue = `between ${moment.tz(filterValue.startValue, dateTimezone).startOf('day').format('Do MMM YYYY')} and ${moment.tz(filterValue.endValue, dateTimezone).endOf('day').format('Do MMM YYYY')}`
      } else if (filterValue.startValue) {
        dateValue = `from ${moment.tz(filterValue.startValue, dateTimezone).startOf('day').format('Do MMM YYYY')}`
      } else {
        dateValue = `until ${moment.tz(filterValue.endValue, dateTimezone).endOf('day').format('Do MMM YYYY')}`
      }
    }
    valuePart = <span className={css(Styles.chipTextValue)}>{CRMHelper.getLabelFromDateId(dateValue) || dateValue}</span>
  } else if (value && typeof value === 'object' && value.type === 'numberRange') {
    valuePart = <span className={css(Styles.chipTextValue)}>{generateNumberRangeValue(value)}</span>
  } else if (Array.isArray(filterValue)) {
    valuePart = (
      <span>
        {filterValue.map((item, index) => (
          <span key={index}>
            <span className={css(Styles.chipTextValue)}>{item}</span>
            {index < filterValue.length - 1 && (key.endsWith('~') ? ' and ' : ' or ')}
          </span>
        ))}
      </span>
    )
  } else {
    valuePart = <span className={css(Styles.chipTextValue)}>{filterValue}</span>
  }

  return <span className={css(Styles.chipContainer)}>{keyPart}{valuePart}</span>
}

function SegmentFilterChips({ filtersQs, filterOptions, timezone }) {
  if (!filtersQs) {
    return <span className={css(Styles.emptyFilter)}>—</span>
  }

  let searchFilters = {}
  try {
    searchFilters = FilterHelper.getFilterFormData(
      filterOptions || [],
      `https://filter.slixta.com?${filtersQs}`,
      timezone || Helper.getLocalTimezone()
    ) || {}
  } catch (err) {
    return <span className={css(Styles.rawFilter)}>{filtersQs}</span>
  }

  const entries = Object.entries(searchFilters).filter(([key, value]) => {
    if (!key || EXCLUDED_CHIP_KEYS.includes(key)) return false
    if (value == null) return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  })

  if (entries.length === 0) {
    return <span className={css(Styles.emptyFilter)}>—</span>
  }

  return (
    <div className={css(Styles.chipsRow)}>
      {entries.map(([key, value]) => {
        const filterField = (filterOptions || []).find((item) => item.key === key)
          || (filterOptions || []).find((item) => item.key === FilterHelper.getFilterBaseKey(key))
        const chipLabel = FilterHelper.getChipLabelForFilterKey(key, filterOptions || [])
        let filterValue = value
        if (filterField && filterField.options) {
          if (Array.isArray(value)) {
            filterValue = value.map((val) => resolveOptionLabel(filterField, key, val)).filter(Boolean)
          } else if (!(value && typeof value === 'object' && (value.type === 'dateRange' || value.type === 'numberRange'))) {
            filterValue = resolveOptionLabel(filterField, key, value)
          }
        }
        return (
          <Chip
            key={key}
            label={buildChipLabel(chipLabel, key, filterValue, searchFilters, timezone)}
            size='medium'
            color={AppTheme.secondaryColor}
            classes={{ root: css(Styles.chipRoot) }}
          />
        )
      })}
    </div>
  )
}

export class Segments2 extends BaseEntityPage {

  constructor(props, options = {}) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = options.pageTitle || Helper.getString('segments')
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.subOrgId = Controller.get().userMgr().getSubOrgId(this.orgId)
    this.teamsUnifiedView = AppHelper.isTeamsUnifiedView(this.orgId)
    this.teamsEnabled = AppHelper.isTeamsEnabled(this.orgId)
    this.isLeadRoutingEnabled = !!AppHelper.getSetting(this.orgId, 'LEAD_ROUTING_TRIGGERS')
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: Helper.getString('segments') },
    ]
    this.toFetch = [
      { key: 'csegments', ms: this.ms, method: 'GET', paths: ['csegments'], queryParams: { orgId: this.orgId, pb: 'fieldDef,fieldDefCSegmentConfig' } },
      { key: 'crmcontacts', ms: this.ms, method: 'GET', paths: ['crmcontacts'], queryParams: { orgId: this.orgId, pb: 'fieldDef', includeCustomFields: 'true', limit: 1 } },
      { key: 'usersORG', ms: this.ms, method: 'GET', paths: ['miscs', 'get-org-users2'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } },
    ]
    if (AppHelper.isABMEnabled(this.orgId)) {
      this.toFetch.push({ key: 'crmaccountnames', ms: this.ms, method: 'GET', paths: ['miscs', 'get-crm-account-titles'], queryParams: { orgId: this.orgId } })
    }
    if (this.teamsEnabled && !this.subOrgId) {
      this.toFetch.push({ key: 'suborgs', ms: this.ms, method: 'GET', paths: ['miscs', 'get-sub-orgs'], queryParams: { orgId: this.orgId, parentOrgId: this.orgId } })
    }
    this.apiData = {
      orgId: this.orgId,
      type: 'FILTER',
    }
    this.pageKey = ((this.props.match && this.props.match.path) || '').includes('segments2')
      ? 'segments2'
      : 'segments'
    this.noAdd = true
    this.tableHeadersMap = {
      Index: (current) => current.__index + 1,
      Name: (current) => current.title,
      Info: (current) => (
        <SegmentFilterChips
          filtersQs={current.filtersQs}
          filterOptions={this.state.filterOptions}
          timezone={current.timeZone || Helper.getLocalTimezone()}
        />
      ),
    }
    this.cellWidths = {
      Index: Styles.cellWidthSmall,
      Name: Styles.cellWidthWide,
      Info: Styles.cellWidthWide,
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
  }

  onFetchSuccess(results) {
    const segmentsResult = this.getResult(results, 'csegments')
    const contactsResult = this.getResult(results, 'crmcontacts')

    this.fieldDef = this.formDefinition = segmentsResult.fieldDef
    const timeZoneField = Helper.getField(segmentsResult.fieldDefCSegmentConfig, 'timeZone')
    if (timeZoneField) {
      this.timeZoneField = { ...timeZoneField, advanced: true }
    }

    let filterOptions = []
    if (contactsResult && contactsResult.fieldDef) {
      const fieldDef = contactsResult.fieldDef
      const usersOrgResult = this.getResult(results, 'usersORG')
      const staffUsers = (usersOrgResult && usersOrgResult.items) || []
      const crmAccountNames = this.getResult(results, 'crmaccountnames')
      const subOrgsResult = this.getResult(results, 'suborgs')
      const subOrgs = this.teamsUnifiedView
        ? AppHelper.getAccessibleTeams(this.orgId)
        : ((subOrgsResult && subOrgsResult.items) || [])

      UiHelper.populateOptions(fieldDef, { items: StaffHelper.uniqueStaffByUserId(staffUsers) }, 'assignee')
      if (Array.isArray(crmAccountNames)) {
        UiHelper.populateOptions(fieldDef, { items: crmAccountNames }, 'accounts')
        UiHelper.populateOptions(fieldDef, { items: crmAccountNames }, 'primaryAccountId')
      }
      if (this.teamsUnifiedView || subOrgsResult) {
        UiHelper.populateOptions(fieldDef, { items: subOrgs }, 'subOrgId')
      }

      const accountFieldDef = (AppHelper.isABMEnabled(this.orgId) && contactsResult.accountFieldDef)
        ? CdpHelper.enrichAccountFieldDefRangeOptions(contactsResult.accountFieldDef, contactsResult.stats)
        : null
      const filterFormDefConf = CRMHelper.buildCrmContactFilterFormDefConf({
        fieldDef,
        orgId: this.orgId,
        accountFieldDef,
        crmContactsStats: contactsResult.stats,
        currentUrl: 'https://filter.slixta.com',
        staffUsers,
        subOrgs,
        isLeadRoutingEnabled: this.isLeadRoutingEnabled,
      })
      const getFilterOptionsResult = CRMHelper.getFilterOptions(
        contactsResult.fieldDef,
        filterFormDefConf,
        'https://filter.slixta.com',
        accountFieldDef
      )
      filterOptions = CRMHelper.processFilterOptions(getFilterOptionsResult.filterOptions || [], this.orgId)
      const stateField = filterOptions.find((f) => f && f.key === 'state')
      if (stateField) {
        UiHelper.removeHiddenCrmContactStates(stateField, this.orgId)
      }
    }

    this.setState({
      items: (segmentsResult.items || []).filter(s => s.type === 'FILTER'),
      filterOptions,
    })
  }

  onAdd() {
    this.formDefinition = this.fieldDef
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['csegments'], queryParams: {orgId: this.orgId} }),
    });
  }

  openModalForEdit(current) {
    this.editId = current.id
    const fieldDef = JSON.parse(JSON.stringify(this.fieldDef))
    if (this.timeZoneField) {
      const fields = fieldDef.steps[0].fields
      const existing = fields.find(f => f.key === 'timeZone')
      if (existing) {
        existing.advanced = true
      } else {
        fields.push(JSON.parse(JSON.stringify(this.timeZoneField)))
      }
    }
    const modalObj = {
      ...current,
      timeZone: current.timeZone || Helper.getLocalTimezone(),
    }
    super.openModalForEdit(modalObj, fieldDef)
  }

  onEdit(current) {
    this.setState({
      apiMethod: 'PUT',
      apiEndPoint: ApiHelper.makeUrlPath2({ ms: this.ms, paths: ['csegments', current.id], queryParams: {orgId: this.orgId} }),
    });
  }

  onActions = (current) => {
    const actions = []
    actions.push(UiHelper.buttonEdit(this, current))
    actions.push(
      <Link to={this.baseRoute() + '/crm-contacts?' + (current.filtersQs || '') + (current.timeZone ? `&_datetimezone=${current.timeZone}` : '')} key={'view-contacts-' + current.id}>
        <Tooltip title='View Contacts' placement='top-start'>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i className='material-icons-outlined'>person</i>
          </span>
        </Tooltip>
      </Link>
    )
    return actions
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) { return '' }
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  cellWidthSmall: {
    width: '10%',
  },
  cellWidthWide: {
    width: '40%',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    maxWidth: '100%',
  },
  chipRoot: {
    height: 'auto',
    minHeight: 32,
    padding: 8,
    backgroundColor: AppTheme.secondaryColor,
  },
  chipContainer: {
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    maxWidth: '100%',
  },
  chipTextValue: {
    fontWeight: 600,
  },
  emptyFilter: {
    color: '#9e9e9e',
  },
  rawFilter: {
    fontSize: 12,
    wordBreak: 'break-all',
  },
})
